/**
 * React Hook: Start Metadata Retrieval
 * 
 * Initiates metadata retrieval from Salesforce via GraphQL mutation
 * 
 * Supports:
 * - Full retrieval (refreshType: "FULL") - fetch all metadata types
 * - Single type retrieval (refreshType: "ApexClass") - fetch specific type
 */

import { useMutation } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { RefreshType } from '@/constants/metadata/refresh_type';

// ============================================================================
// Types
// ============================================================================

export interface FetchMetadataTypeInput {
  /** Existing retrieval ID */
  retrievalId: string;
  /** Organization ID */
  organizationId: string;
  /** 
   * Type of refresh:
   * - "FULL" - fetch all metadata types
   * - "METADATA_TYPE" - fetch specific type (requires metadataType field)
   */
  refreshType: RefreshType;
  /**
   * Specific metadata type to refresh (e.g., "ApexClass", "ApexTrigger")
   * Required when refreshType is "METADATA_TYPE"
   */
  metadataType?: string;
}

export interface FetchMetadataTypeResponse {
  fetchMetadataTypeFromSalesforce: {
    /** Retrieval ID (same as input, used for progress subscription) */
    retrievalId: string;
    /** The refresh type that was requested */
    refreshType: string;
    /** Current status */
    status: string;
    /** Status message */
    message: string;
  };
}

// ============================================================================
// GraphQL
// ============================================================================

const FETCH_METADATA_TYPE_MUTATION = `
  mutation FetchMetadataType($input: FetchMetadataTypeFromSalesforceInput!) {
    fetchMetadataTypeFromSalesforce(input: $input) {
      retrievalId
      refreshType
      status
      message
    }
  }
`;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to start metadata retrieval from Salesforce
 * 
 * @example
 * ```tsx
 * const { startRetrieval, isPending, error } = useStartRetrieval();
 * 
 * // Full retrieval (all metadata types)
 * const result = await startRetrieval({
 *   retrievalId: 'ret-123',
 *   organizationId: 'org-456',
 *   refreshType: 'FULL',
 * });
 * 
 * // Single type retrieval
 * const result = await startRetrieval({
 *   retrievalId: 'ret-123',
 *   organizationId: 'org-456',
 *   refreshType: 'ApexClass',
 * });
 * 
 * // Then subscribe to progress using useRetrievalProgress
 * subscribeToProgress(result.retrievalId);
 * ```
 */
export function useStartRetrieval() {
  const mutation = useMutation({
    mutationFn: async (input: FetchMetadataTypeInput) => {
      const response = await graphqlRequest<FetchMetadataTypeResponse>(
        FETCH_METADATA_TYPE_MUTATION,
        { input }
      );
      
      // Handle null response (GraphQL may return null with errors)
      if (!response.fetchMetadataTypeFromSalesforce) {
        throw new Error('Failed to start metadata retrieval');
      }
      
      return response.fetchMetadataTypeFromSalesforce;
    },
    onSuccess: (data) => {
      console.log('✅ Metadata retrieval started:', {
        retrievalId: data.retrievalId,
        refreshType: data.refreshType,
        status: data.status,
      });
      console.log('🚀 Retrieval started, retrievalId:', data.retrievalId);
    },
    onError: (error) => {
      console.error('❌ Failed to start metadata retrieval:', error);
    },
  });

  return {
    /** Start metadata retrieval */
    startRetrieval: mutation.mutateAsync,
    /** Whether mutation is in progress */
    isPending: mutation.isPending,
    /** Mutation error */
    error: mutation.error,
    /** Reset mutation state */
    reset: mutation.reset,
  };
}
